import React from "react"
import { bool, number, string, arrayOf, shape } from "prop-types"
import { Automations as CoreAutomations } from "@planningcenter/core-automations"
import { theme } from "components/admin/registrations_theme_provider"

const propTypes = {
  canCreate: bool.isRequired,
  currentPersonACID: number.isRequired,
  detailedSignup: bool.isRequired,
  dateFormat: string.isRequired,
  eventId: number.isRequired,
  selectionTypes: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired
    })
  ).isRequired,
  incomingAutomationsParam: string,
  olsonTimeZone: string.isRequired,
  triggerResource: string.isRequired,
  twentyFourHourTime: bool.isRequired,
  hasWaitlistAvailable: bool
}

const buttonThemes = theme.button.themes
const colors = theme.colors
const automationsTheme = {
  ...theme,
  button: {
    themes: {
      ...buttonThemes,
      primary: {
        fill: {
          ...buttonThemes.create.fill,
          active: { ...buttonThemes.create.fill.hover }
        },
        outline: {
          ...buttonThemes.default.outline,
          active: {
            backgroundColor: "var(--default-outline-btn-stroke-color)"
          }
        },
        naked: { ...buttonThemes.default.naked }
      }
    }
  },
  colors: {
    ...colors,
    home: "var(--t-fill-color-product-home-050)"
  }
}

export default function SignupAutomations({
  canCreate,
  currentPersonACID,
  dateFormat,
  eventId,
  selectionTypes,
  incomingAutomationsParam,
  olsonTimeZone,
  triggerResource,
  twentyFourHourTime,
  detailedSignup,
  hasWaitlistAvailable
}) {
  const triggerCondition = {
    data: { relationships: { event: { data: { id: eventId.toString() } } } }
  }

  const selectionTypeCondition = {
    id: "data.relationships.attendee_type.data.id",
    description: "With a selection type...",
    kind: "Select",
    list: [
      { description: "Any", value: "no-option" },
      ...selectionTypes.map(selectionType => ({
        description: selectionType.name,
        value: selectionType.id.toString()
      }))
    ]
  }

  const statusCondition = {
    id: "data.attributes.status",
    description: "With a status...",
    kind: "Select",
    list: [
      { description: "Any", value: "no-option" },
      { description: "Active", value: "active" },
      { description: "Waitlist", value: "waitlisted" }
    ],
    defaultValue: "active",
    renderIf: hasWaitlistAvailable
  }

  const detailedSignupTriggers = [
    {
      description: "Is registered as a new attendee",
      name: "registrations.v2.events.attendee.created",
      dynamicConditions: [selectionTypeCondition, statusCondition]
    },
    {
      description: "Is canceled as an attendee",
      name: "registrations.v2.events.attendee.cancelled",
      dynamicConditions: [selectionTypeCondition]
    }
  ]

  const simpleSignupTriggers = [
    {
      description: "Is the registration contact on a new registration",
      name: "registrations.v2.events.registration.created"
    },
    {
      description: "Is the registration contact on a canceled registration",
      name: "registrations.v2.events.registration.cancelled"
    }
  ]

  const triggerEvents = [...(detailedSignup ? detailedSignupTriggers : []), ...simpleSignupTriggers]

  return (
    <CoreAutomations
      blankStateDescription="With automations, you can automatically perform an action when someone registers or cancels for this signup."
      blankStateLink="https://pcoregistrations.zendesk.com/hc/en-us/articles/16689375475867"
      currentPersonCanCreate={canCreate}
      currentPersonId={currentPersonACID}
      defaultApp="people"
      incomingAutomationsParam={incomingAutomationsParam}
      theme={automationsTheme}
      trigger={{
        resource: triggerResource,
        conditions: triggerCondition,
        events: triggerEvents
      }}
      currentOrganization={{
        dateFormat,
        olsonTimeZone,
        twentyFourHourTime
      }}
    />
  )
}

SignupAutomations.propTypes = propTypes
